/* import __COLOCATED_TEMPLATE__ from './question-creation-empty-state.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type PlaygroundApiService from 'embercom/services/playground-api';
import {
  ExtractionType,
  type PlaygroundQuestionGeneration,
} from 'embercom/lib/fin-playground/question-generation';
import { TOPICS_EXPLORER_HELP_URL } from 'embercom/lib/fin-playground/topics';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import { type SafeString } from '@ember/template/-private/handlebars';

interface Signature {
  Args: {
    questionGeneration?: PlaygroundQuestionGeneration;
    openNewGroupModal?: (place: string) => void;
    selectAddQuestionDropdownOption?: (option: string) => void;
  };
}

export default class QuestionCreationEmptyState extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare playgroundApi: PlaygroundApiService;

  ExtractionType = ExtractionType;

  @action
  trackLearnMoreClick(event: Event) {
    let target = event.target as HTMLElement;
    if (target.getAttribute('data-track-learn-more') === 'true') {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'learn_more_insights_topics_link',
        context: 'playground',
        place: 'question_creation_empty_state_tooltip',
      });
    }
  }

  get learnMoreLinkHtml(): SafeString {
    return sanitizeHtml(
      `<a href="${TOPICS_EXPLORER_HELP_URL}" target="_blank" class="underline" data-track-learn-more="true">${this.intl.t('ai-agent.playground.generate-dropdown.learn-more')}</a>`,
    );
  }

  get isQuestionGenerationDisabledByPlatform() {
    return (
      this.playgroundApi.hasDisconnectedStandalonePlatform ||
      this.playgroundApi.hasUnsupportedStandalonePlatforms
    );
  }

  getQuestionGenerationTooltip() {
    if (this.args.questionGeneration?.isJobRunningForApp) {
      return this.intl.t(
        'ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip-running',
      );
    }
    if (this.playgroundApi.hasUnsupportedStandalonePlatforms) {
      return this.intl.t(
        'ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip-unsupported',
      );
    }
    if (this.playgroundApi.hasDisconnectedStandalonePlatform) {
      return this.intl.t(
        'ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip-no-sync',
      );
    }
    return undefined;
  }

  get addQuestionDropdownOptions() {
    let inboxItems = [];
    let othersItems = [];

    // Generate from all conversations option
    let isGenerateQuestionsDisabled =
      this.args.questionGeneration?.isJobRunningForApp ||
      this.isQuestionGenerationDisabledByPlatform ||
      !this.args.questionGeneration?.isPossible;

    let generateQuestionsTooltip =
      this.getQuestionGenerationTooltip() ||
      (!this.args.questionGeneration?.isPossible
        ? this.intl.t('ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip')
        : undefined);

    inboxItems.push({
      text: this.intl.t(
        'ai-agent.playground.blank-state.generate-questions-from-all-conversations',
      ),
      value: 'generate-questions',
      isDisabled: isGenerateQuestionsDisabled,
      tooltip: generateQuestionsTooltip,
    });

    // Generate by topic option
    if (this.appService.app.canUseFinPlaygroundTopics) {
      let isGenerateByTopicDisabled =
        this.args.questionGeneration?.isJobRunningForApp ||
        this.isQuestionGenerationDisabledByPlatform ||
        !this.args.questionGeneration?.hasTopics;

      let generateQuestionsByTopicTooltip =
        this.getQuestionGenerationTooltip() ||
        (!this.args.questionGeneration?.hasTopics
          ? String(
              this.intl.t('ai-agent.playground.generate-dropdown.by-topic-disabled-tooltip', {
                learnMoreLinkHtml: this.learnMoreLinkHtml,
                htmlSafe: true,
              }),
            )
          : undefined);

      inboxItems.push({
        text: this.intl.t('ai-agent.playground.add-questions-dropdown.generate-questions-by-topic'),
        value: 'generate-questions-by-topic',
        isDisabled: isGenerateByTopicDisabled,
        tooltip: generateQuestionsByTopicTooltip,
        onTooltipClick:
          !this.args.questionGeneration?.hasTopics &&
          !this.args.questionGeneration?.isJobRunningForApp
            ? this.trackLearnMoreClick
            : undefined,
      });

      // Track when "Generate by topic" option is visible
      this.intercomEventService.trackAnalyticsEvent({
        action: 'rendered',
        object: 'generate_by_topic_cta',
        context: 'playground',
        place: 'question_creation_empty_state_dropdown',
        is_enabled: this.args.questionGeneration?.hasTopics,
      });
    }

    othersItems.push(
      {
        text: this.intl.t('ai-agent.playground.add-questions-dropdown.upload-csv'),
        value: 'upload-csv',
      },
      {
        text: this.intl.t('ai-agent.playground.add-manually'),
        value: 'add-question',
      },
    );

    let createNewGroupItem = {
      text: this.intl.t('ai-agent.playground.add-questions-dropdown.create-new-test'),
      value: 'create-new-test',
      icon: 'new',
      onSelectItem: () => {
        if (this.args.openNewGroupModal) {
          this.args.openNewGroupModal('groups-empty-state-screen');
        }
      },
    };

    let groups = [];

    if (inboxItems.length > 0) {
      groups.push({
        heading: this.appService.app.canUseStandalone
          ? this.intl.t('ai-agent.playground.add-questions-dropdown.conversations-heading')
          : this.intl.t('ai-agent.playground.add-questions-dropdown.inbox-heading'),
        items: inboxItems,
      });
    }

    groups.push({
      heading: this.intl.t('ai-agent.playground.add-questions-dropdown.others-heading'),
      items: othersItems,
    });

    groups.push({
      items: [createNewGroupItem],
    });

    return groups;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::QuestionCreationEmptyState': typeof QuestionCreationEmptyState;
    'ai-agent/playground/question-creation-empty-state': typeof QuestionCreationEmptyState;
  }
}
